import React, { useState, useEffect } from 'react';
import { message, Spin } from 'antd';
import FinanceSummaryCard from './FinanceSummaryCard';
import FinanceRecordList from './FinanceRecordList';
import AddFinanceRecordModal from './AddFinanceRecordModal';
import {
  getFinanceSummary,
  getFinanceRecords,
  deleteFinanceRecord,
  updateContractAmount,
  EntityType,
} from '../../services/finance';
import type { FinanceSummary, FinanceRecord } from '../../types/finance';

interface FinanceTabProps {
  leadId: number;
  lead: any;
  canEdit: boolean;
}

/**
 * 财务情况标签页组件
 * 集成财务概览、财务记录列表和新增财务记录功能
 */
const FinanceTab: React.FC<FinanceTabProps> = ({ leadId, lead, canEdit }) => {
  // State management
  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [records, setRecords] = useState<FinanceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  /**
   * Load finance summary
   */
  const loadFinanceSummary = async () => {
    try {
      const data = await getFinanceSummary(EntityType.LEAD, leadId);
      // Convert string amounts to numbers if needed
      const normalizedSummary: FinanceSummary = {
        contract_amount: typeof data.contract_amount === 'string' 
          ? parseFloat(data.contract_amount) 
          : data.contract_amount,
        total_received: typeof data.total_received === 'string' 
          ? parseFloat(data.total_received) 
          : data.total_received,
        total_refund: typeof data.total_refund === 'string' 
          ? parseFloat(data.total_refund) 
          : data.total_refund,
        total_receivable: typeof data.total_receivable === 'string' 
          ? parseFloat(data.total_receivable) 
          : data.total_receivable,
        net_received: typeof data.net_received === 'string' 
          ? parseFloat(data.net_received) 
          : data.net_received,
      };
      setSummary(normalizedSummary);
    } catch (error: any) {
      console.error('Failed to load finance summary:', error);
      // Don't show error message for summary as it might not exist yet
    }
  };

  /**
   * Load finance records
   */
  const loadFinanceRecords = async () => {
    try {
      const data = await getFinanceRecords(EntityType.LEAD, leadId);
      // Convert string amounts to numbers if needed and ensure required fields
      const normalizedRecords = (data.items || []).map(record => ({
        ...record,
        amount: typeof record.amount === 'string' 
          ? parseFloat(record.amount) 
          : record.amount,
        updated_at: record.updated_at || record.created_at, // Ensure updated_at is always present
      }));
      setRecords(normalizedRecords);
    } catch (error: any) {
      console.error('Failed to load finance records:', error);
      message.error('加载财务记录失败');
    }
  };

  /**
   * Load all finance data
   */
  const loadFinanceData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadFinanceSummary(), loadFinanceRecords()]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refresh finance data
   */
  const refreshFinanceData = () => {
    setRefreshKey((prev) => prev + 1);
  };

  /**
   * Handle update contract amount
   */
  const handleUpdateContractAmount = async (amount: number) => {
    try {
      await updateContractAmount(EntityType.LEAD, leadId, amount);
      // Refresh summary to show updated contract amount
      await loadFinanceSummary();
    } catch (error: any) {
      console.error('Failed to update contract amount:', error);
      throw error;
    }
  };

  /**
   * Handle delete finance record
   */
  const handleDeleteRecord = async (recordId: number) => {
    try {
      await deleteFinanceRecord(EntityType.LEAD, leadId, recordId);
      message.success('财务记录已删除');
      // Refresh both summary and records after deletion
      refreshFinanceData();
    } catch (error: any) {
      console.error('Failed to delete finance record:', error);
      message.error('删除失败，请重试');
    }
  };

  /**
   * Handle add finance record success
   */
  const handleAddSuccess = () => {
    // Refresh both summary and records after adding new record
    refreshFinanceData();
  };

  /**
   * Handle open add modal
   */
  const handleOpenAddModal = () => {
    setModalVisible(true);
  };

  /**
   * Handle close add modal
   */
  const handleCloseAddModal = () => {
    setModalVisible(false);
  };

  // Load finance data on mount and when refreshKey changes
  useEffect(() => {
    if (leadId) {
      loadFinanceData();
    }
  }, [leadId, refreshKey]);

  // Show loading state
  if (loading && records.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" tip="加载财务数据中..." />
      </div>
    );
  }

  return (
    <div className="finance-tab" style={{ padding: '16px' }}>
      {/* Finance Summary Card */}
      <FinanceSummaryCard
        summary={summary}
        contractAmount={summary?.contract_amount ?? lead?.contract_amount}
        canEdit={canEdit}
        onUpdateContractAmount={handleUpdateContractAmount}
      />

      {/* Finance Record List */}
      <FinanceRecordList
        records={records}
        leadId={leadId}
        canEdit={canEdit}
        loading={loading}
        onAdd={handleOpenAddModal}
        onDelete={handleDeleteRecord}
        onRefresh={refreshFinanceData}
      />

      {/* Add Finance Record Modal */}
      <AddFinanceRecordModal
        visible={modalVisible}
        leadId={leadId}
        onCancel={handleCloseAddModal}
        onSuccess={handleAddSuccess}
      />
    </div>
  );
};

export default FinanceTab;
